import React, { useMemo, useState } from 'react';
import { Button, Col, Flex, Popover, Row, Select, Space } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import { CSVLink } from 'react-csv';
import { useQuery } from 'react-query';
import { Moment } from 'moment';
import { formatCurrency } from '@shared/utils/currency';
import { getReceivableLabel, longReceivableRanges, shortReceivableRanges } from '@shared/utils/receivablesHelpers';
import { CsvDelimiter, DashboardPeriods, DashboardTypes, DaysDashboardPeriods, NumberFormatStyle } from '@shared/utils/constants';
import type { PayablesAgingItem } from '@api/features/statistics/getPayablesAgingData';
import { useAuth } from '@client/lib/auth';
import { getPayablesAgingData } from '@client/lib/api';
import Table from '@client/components/Table/Table';
import useNumberFormatStyle from '@client/utils/hooks/useExportNotation';
import { useDashboardCurrency, useDashboardPeriod, DashboardComponent } from '../helpers/dashboardInfo';
import styles from './PayablesAgingDashboard.module.css';
import PayablesOutstandingsTableSection from './PayablesOutstandingsTableSection';
import { buildPayablesAgingCsvData } from './payablesAgingCsv';

type DashboardRow = {
	counterparty: string | null;
	notDuePayable: number;
	overflowPayable: number;
	total: number;
	payable: {
		range: [Moment, Moment];
		label: string;
		payable: number;
	}[];
};

type DashboardTotalRow = {
	isTotal: true;
	counterparty: React.ReactNode;
	totals: number[];
	notDuePayable: number;
	overflowPayable: number;
	total: number;
};

type TableRow = DashboardRow | DashboardTotalRow;

const isTotalRow = (row: TableRow): row is DashboardTotalRow => (row as DashboardTotalRow).isTotal === true;

const PayablesAgingDashboard: DashboardComponent = ({ setLastUpdated }) => {
	const { userInfo } = useAuth();
	const { formatStyle, setFormatStyle } = useNumberFormatStyle(NumberFormatStyle.UkUs);
	const [tableRows, setTableRows] = useState<PayablesAgingItem[]>([]);

	const period = useDashboardPeriod();
	const currencyCtx = useDashboardCurrency();
	const currency = currencyCtx?.currency;
	const daysPeriod = (period ?? DashboardPeriods.SIXTY_DAYS) as DaysDashboardPeriods;

	const { data, isFetching: loading } = useQuery(
		[DashboardTypes.AGED_PAYABLES, period],
		() => getPayablesAgingData(daysPeriod),
		{
			onSuccess: (d) => {
				setLastUpdated(d.lastUpdated);

				if (!currencyCtx) {
					return;
				}

				const availableCurrencies = d.availableCurrencies ?? [];
				currencyCtx.setAvailableCurrencies(availableCurrencies);

				const current = currencyCtx.currency;

				if (current && availableCurrencies.includes(current)) {
					return;
				}

				const next = d.currency ?? availableCurrencies[0];

				if (next) {
					currencyCtx.setCurrency(next);
				}
			},
			refetchOnWindowFocus: false,
		},
	);

	const selectedCurrency = currency ?? data?.currency ?? userInfo.baseCurrency;
	const dashboardData = data?.dataByCurrency?.[selectedCurrency] ?? [];
	const detailData = data?.itemsByCurrency?.[selectedCurrency] ?? [];

	const ranges = daysPeriod === DashboardPeriods.SIXTY_DAYS ? shortReceivableRanges : longReceivableRanges;

	const tableData = useMemo<TableRow[]>(() => {
		const totalsByRange = ranges.map((_r, i) =>
			dashboardData.reduce((t, cp) => t + (cp.payable[i]?.payable ?? 0), 0),
		);

		const totalNotDue = dashboardData.reduce((t, cp) => t + cp.notDuePayable, 0);
		const totalOverflow = dashboardData.reduce((t, cp) => t + cp.overflowPayable, 0);
		const grandTotal = dashboardData.reduce((t, cp) => t + cp.total, 0);

		const totalRow: DashboardTotalRow = {
			isTotal: true,
			counterparty: <b>Total</b>,
			totals: totalsByRange,
			notDuePayable: totalNotDue,
			overflowPayable: totalOverflow,
			total: grandTotal,
		};

		return [...dashboardData, totalRow];
	}, [dashboardData, ranges]);

	const csvData = useMemo(() => {
		return buildPayablesAgingCsvData({
			title: 'Aged Payables',
			daysPeriod,
			ranges,
			formatStyle,
			summaryRows: dashboardData,
			detailRows: tableRows,
		});
	}, [daysPeriod, ranges, formatStyle, dashboardData, tableRows]);

	return (
		<>
			<Row gutter={[16, 16]}>
				<Col span={24}>
					<Flex justify="flex-end" style={{ marginBottom: 12 }}>
						<Popover
							trigger="click"
							content={(
								<Space direction="vertical">
									<Select
										className={styles.widerSelect}
										value={formatStyle}
										onChange={(v) => setFormatStyle(v)}
										options={[
											{ label: 'UK/US notation', value: NumberFormatStyle.UkUs },
											{ label: 'European notation', value: NumberFormatStyle.European },
										]}
									/>
									<CSVLink
										data={csvData}
										separator={formatStyle === NumberFormatStyle.UkUs ?
											CsvDelimiter.Comma :
											CsvDelimiter.Semicolon
										}
										enclosingCharacter=""
										filename={`Aged Payables ${new Date().toISOString().slice(0, 10)}.csv`}
									>
										<Button type="primary" icon={<ExportOutlined />}>
											Export
										</Button>
									</CSVLink>
								</Space>
							)}
						>
							<Button icon={<ExportOutlined />}>Export all to CSV</Button>
						</Popover>
					</Flex>

					<Table<TableRow>
						size="small"
						pagination={false}
						dataSource={tableData}
						loading={loading}
						columns={[
							{
								dataIndex: 'counterparty',
								title: 'Counterparty',
							},
							{
								title: 'Not Due',
								dataIndex: 'notDuePayable',
								sorter: (a: any, b: any) => (a.notDuePayable ?? 0) - (b.notDuePayable ?? 0),
								render: (_value: number, row: TableRow) => {
									const formatted = formatCurrency(row.notDuePayable, selectedCurrency);

									return isTotalRow(row) ? <b>{formatted}</b> : formatted;
								},
							},
							...ranges.map((_r, i) => ({
								title: getReceivableLabel(i, daysPeriod),
								sorter: (a: any, b: any) => {
									const aVal = isTotalRow(a) ? a.totals[i] : (a.payable?.[i]?.payable ?? 0);
									const bVal = isTotalRow(b) ? b.totals[i] : (b.payable?.[i]?.payable ?? 0);

									return aVal - bVal;
								},
								render: (_value: number | undefined, row: TableRow) => {
									const value = isTotalRow(row) ? row.totals[i] : (row.payable[i]?.payable ?? 0);
									const formatted = formatCurrency(value, selectedCurrency);

									return isTotalRow(row) ? <b>{formatted}</b> : formatted;
								},
							})),
							{
								title: daysPeriod === DashboardPeriods.SIXTY_DAYS ? '+60 days' : '+120 days',
								dataIndex: 'overflowPayable',
								sorter: (a: any, b: any) => (a.overflowPayable ?? 0) - (b.overflowPayable ?? 0),
								render: (_value: number, row: TableRow) => {
									const formatted = formatCurrency(row.overflowPayable, selectedCurrency);

									return isTotalRow(row) ? <b>{formatted}</b> : formatted;
								},
							},
							{
								title: 'Total',
								dataIndex: 'total',
								defaultSortOrder: 'descend',
								sorter: (a: any, b: any) => (a.total ?? 0) - (b.total ?? 0),
								render: (total: number, row: TableRow) => {
									const formatted = formatCurrency(total, selectedCurrency);

									return isTotalRow(row) ? <b>{formatted}</b> : formatted;
								},
							},
						]}
					/>
				</Col>
			</Row>

			<Row gutter={[16, 16]} style={{ marginTop: 24 }}>
				<Col span={24}>
					<PayablesOutstandingsTableSection
						title="Payables"
						className={styles.table}
						setLastUpdated={(m) => setLastUpdated?.(m)}
						onFilteredRowsChange={setTableRows}
						data={detailData}
						loading={loading}
					/>
				</Col>
			</Row>
		</>
	);
};

export default PayablesAgingDashboard;

